import {uniqBy} from 'lodash-es';

import {
  getTagboards,
  getMultipleTagboards,
  getTags,
} from '../../../services/tagboards';

const state = {
  tagboards: [],
  tagboardTags: [],
  lookupTable: {},

  isLoading: false,
  page: 1,
  limit: 20,
  total: 0,
  tags: [],
  search: '',
};

const actions = {
  async getTagboards(context) {
    const { production } = context.rootState.production;
    const { owner } = production;

    const { tags, page, limit, search } = context.state;

    context.commit('SET_BOARDS_FIELD', { key: 'isLoading', value: true });

    const resp = await getTagboards(owner, {
      page,
      limit,
      tags: tags.join(','),
      search,
    });

    const { tagboards, total } = resp;

    context.commit('SET_BOARDS_FIELD', { key: 'isLoading', value: false });
    context.commit('SET_BOARDS', tagboards);
    context.commit('ADD_BOARDS_TO_LOOKUP_TABLE', tagboards);
    context.commit('SET_BOARDS_FIELD', { key: 'total', value: total });
  },

  async getTagboardTags({ commit, rootState }) {
    const { owner } = rootState.production.production;
    const { tags } = await getTags(owner);
    const sortedTags = [...tags].sort((a, b) => a.localeCompare(b));

    commit('SET_BOARDS_FIELD', { key: 'tagboardTags', value: sortedTags });
  },

  async getTagboard(context, { tagboardId }) {
    const { production } = context.rootState.production;
    const { owner } = production;

    const resp = await getMultipleTagboards(owner, [tagboardId]).catch((err) => {
      console.error('Failed to fetch tagboard by ID:', tagboardId, err);
    });

    if (resp?.tagboards?.length) {
      context.commit('ADD_BOARDS_TO_LOOKUP_TABLE', resp.tagboards);
    }
  },

  /**
   * Loop over production timeline and grab any tagboards in themes.
   * Then fetch only those tagboards from the API to fill the lookup table.
   */
  async getTimelineTagboards(context) {
    const { production } = context.rootState.production;
    const { timeline } = production;
    const { owner } = production;

    const tagboardIds = timeline.reduce((memo, post) => {
      if (post?.posts?.length) {
        const [timelinePost] = post.posts;

        if (timelinePost?.type === 'tagboard') {
          return memo.add(timelinePost._id);
        }
      }

      return memo;
    }, new Set());

    const resp = await getMultipleTagboards(owner, [...tagboardIds]).catch((err) => {
      console.error('Failed to fetch tagboards by ID(s)', tagboardIds, err);
    });

    if (resp?.tagboards?.length) {
      context.commit('ADD_BOARDS_TO_LOOKUP_TABLE', resp.tagboards);
    }
  },

  /**
   * Clear list of boards
   */
  resetTagboards(context) {
    context.commit('RESET_BOARDS');
  },

  /**
   * Increment page and fetch next page of data
   */
  incrementBoardsPage(context) {
    context.commit('SET_BOARDS_FIELD', { key: 'page', value: context.state.page + 1 });
    context.dispatch('getTagboards');
  },

  changeSocialBoardSearch({ commit, dispatch }, search) {
    commit('SET_BOARDS_FIELD', { key: 'search', value: search });
    commit('RESET_BOARDS');
    dispatch('getTagboards');
  },

  changeSocialBoardTags({ commit, dispatch }, tags) {
    commit('SET_BOARDS_FIELD', { key: 'tags', value: tags });
    commit('RESET_BOARDS');
    dispatch('getTagboards');
  },

  /**
   * Clear all search tags and fetch boards
   */
  resetSocialBoardSearch(context) {
    context.commit('CLEAR_BOARDS_TAGS');
    context.commit('SET_BOARDS_FIELD', { key: 'search', value: '' });
    context.commit('RESET_BOARDS');
    context.dispatch('getTagboards');
  },

  setTagboards(context, payload) {
    context.commit('SET_BOARDS', payload);
  },
};

const mutations = {
  /* eslint-disable no-shadow, no-param-reassign */

  SET_BOARDS(state, boards) {
    state.tagboards = uniqBy(
      state.tagboards.concat(
        boards.map((board) => ({
          ...board,

          // Additional fields (a la computed properties)
          title: board.name,
        })),
      ),
      '_id',
    );
  },

  ADD_BOARDS_TO_LOOKUP_TABLE(state, boards) {
    if (!boards) { return; }

    state.lookupTable = {
      ...state.lookupTable,
      ...(boards.reduce((memo, board) => ({
        ...memo,
        [board._id]: {
          ...board,
          title: board.name, // Lots of components currently rely on title being a real thing
        },
      }), {})),
    };
  },

  RESET_BOARDS(state) {
    state.tagboards = [];
    state.page = 1;
  },

  /**
  * Generic mutator for setting any field to the provided value
  */
  SET_BOARDS_FIELD(state, { key, value }) {
    state[key] = value;
  },

  ADD_BOARDS_TAG(state, tag) {
    state.tags = state.tags.concat([tag]);
  },

  REMOVE_BOARDS_TAG(state, tag) {
    state.tags = state.tags.filter((t) => t !== tag);
  },

  CLEAR_BOARDS_TAGS(state) {
    state.tags = [];
  },
};

const getters = {
  hasMoreBoards(state) {
    return state.total > (state.page * state.limit);
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
